import { useState } from 'react';
import { CircleCheck, Download, FileText, Sparkles, TriangleAlert } from 'lucide-react';
import { Dialog } from '@dru/ui';
import { newIdempotencyKey } from '@dru/api-client';
import type { HealthReportExplanationView } from '@dru/types';
import { api } from '../lib/api';
import { errorMessage } from '../lib/auth';
import { useQuery } from '../lib/useQuery';
import { useOfflineQuery, savedAgo } from '../lib/useOfflineQuery';
import { checkupType, shortDate } from '../lib/labels';

/**
 * Released results only.
 *
 * An unreleased result is not a result — it is a machine reading that may
 * still be repeated, corrected or rejected. Showing it would turn every draft
 * into a finding the worker has already read, and a later correction into a
 * contradiction nobody can explain to them. The API enforces this; the copy
 * below explains why a test they know they took is not here yet.
 */

interface Result {
  id: string;
  valueNumeric: number | null;
  valueText: string | null;
  valueBoolean: boolean | null;
  unit: string | null;
  flag: string;
  /** Written by the examiner as text; recorded, never interpreted. */
  freeText?: boolean;
  releasedAt: string;
  test: { code: string; name: string };
}

/**
 * A lab flag, in words, with the semantic tone for it.
 *
 * These were painted in the FITNESS colours — a high cholesterol in the amber
 * that means "fit with restrictions", a critical value in the red that means
 * "unfit". A lab value is not a fitness decision, and a worker who has just
 * read "Unfit" in that red on the next tab should not see it again here on a
 * blood sugar. The words carry the meaning; the mark and colour back them up.
 */
const FLAG_LABEL: Record<string, { text: string; tone: string; mark: string }> = {
  NORMAL: { text: 'Normal', tone: 'text-success-600', mark: '✓' },
  LOW: { text: 'Below the usual range', tone: 'text-warning-600', mark: '↓' },
  HIGH: { text: 'Above the usual range', tone: 'text-warning-600', mark: '↑' },
  CRITICAL_LOW: { text: 'Much lower than usual — talk to your doctor', tone: 'text-danger-600', mark: '!' },
  CRITICAL_HIGH: { text: 'Much higher than usual — talk to your doctor', tone: 'text-danger-600', mark: '!' },
  ABNORMAL: { text: 'Not normal — talk to your doctor', tone: 'text-warning-600', mark: '!' },
};

function value(r: Result): string {
  if (r.valueNumeric !== null) return `${r.valueNumeric}${r.unit ? ` ${r.unit}` : ''}`;
  if (r.valueText) return r.valueText;
  if (r.valueBoolean !== null) return r.valueBoolean ? 'Positive' : 'Negative';
  return '—';
}

interface LabReportSummary {
  id: string;
  reportNumber: string;
  status: string;
  revision: number;
  examinationType: string;
  finalisedAt: string | null;
  laboratory: { name: string; city: string | null } | null;
  lineCount: number;
  abnormalCount: number;
  hasDocument: boolean;
}

/**
 * The signed laboratory report, as a document the worker can keep.
 *
 * Above the line-by-line results because it is the thing with the number on
 * it — what a worker hands to their own doctor. Reached through a grant that
 * dies in minutes, exactly like the certificate: a medical document never
 * gets a durable URL, and one saved to the phone is the worker's own copy.
 */
interface HealthReportSummary {
  id: string;
  reportNumber: string;
  status: string;
  revision: number;
  examinationType: string | null;
  attendedAt: string | null;
  issuedAt: string;
  summary: { measured: number; withRange: number; inRange: number; low: number; high: number; critical: number; abnormal: number; panelsTotal: number };
  fitness: { decisionLabel: string; certificateNumber: string | null; validUntil: string | null; retestDueDate: string | null } | null;
  hasDocument: boolean;
}

/**
 * The plain-language explanation of a health report.
 *
 * On the shared Dialog so it behaves like every other modal in the product:
 * focus moves in and comes back, Escape closes it, and the page behind does
 * not scroll. It was a hand-rolled overlay with none of that, a bare "✕" for
 * a close button, and a palette (indigo, emerald, amber) that ignored the
 * theme — in dark mode it was a pale slab with pale text on it.
 */
function AiExplanationModal({ reportId, onClose }: { reportId: string; onClose: () => void }) {
  const [lang, setLang] = useState<'en' | 'hi'>('hi');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const hi = lang === 'hi';

  const query = useQuery<HealthReportExplanationView>(
    () => api.get(`/me/health-reports/${reportId}/explanation?language=${lang}`),
    [reportId, lang]
  );

  async function run(action: () => Promise<unknown>, fallback: string) {
    setBusy(true); setActionError(null);
    try {
      await action();
      query.reload();
    } catch (e) {
      setActionError(errorMessage(e) || fallback);
    } finally {
      setBusy(false);
    }
  }

  // The server creates the payment order and only the provider's
  // confirmation unlocks the report; this page never marks anything paid.
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const handlePurchase = () => run(async () => {
    const st = await api.post<{ order: { checkoutUrl: string | null } | null }>(
      `/me/health-reports/${reportId}/smart-report/orders`, { language: lang }, newIdempotencyKey());
    const url = st.order?.checkoutUrl ?? null;
    setCheckoutUrl(url);
    if (url) window.open(url, '_blank', 'noopener');
  }, 'Payment could not start.');
  const handleRetry = () => run(() => api.post(`/me/health-reports/${reportId}/smart-report/retry`, { language: lang }), 'Retry failed.');
  const handleRefund = () => run(() => api.post(`/me/health-reports/${reportId}/smart-report/refund`, {
    language: lang,
    reason: 'Generation failure refund requested',
  }, newIdempotencyKey()), 'Refund failed.');

  const exp = query.data;
  // The price the server holds, not a number typed into the button.
  const price = `₹${Math.round((exp?.amountPaise ?? 2000) / 100)}`;

  return (
    <Dialog title={hi ? 'रिपोर्ट सरल भाषा में' : 'Your report in simple words'} onClose={onClose} wide>
      <div className="space-y-4" lang={lang}>
        <div role="group" aria-label="Language" className="inline-flex rounded-lg border border-neutral-300 bg-white p-0.5 text-sm font-medium">
          {([['hi', 'हिन्दी'], ['en', 'English']] as const).map(([code, name]) => (
            <button
              key={code}
              type="button"
              lang={code}
              aria-pressed={lang === code}
              onClick={() => setLang(code)}
              className={`min-h-10 rounded-md px-4 ${
                lang === code ? 'bg-primary-600 text-white' : 'text-neutral-700 active:bg-neutral-100'
              }`}
            >
              {name}
            </button>
          ))}
        </div>

        {actionError && (
          <div role="alert" className="rounded-lg bg-danger-100 p-3 text-sm text-danger-600">{actionError}</div>
        )}

        {query.loading && !exp ? (
          <div role="status" className="py-12 text-center text-sm text-neutral-600">
            {hi ? 'विवरण लोड हो रहा है…' : 'Loading…'}
          </div>
        ) : query.error && !exp ? (
          <div className="space-y-3">
            <div role="alert" className="rounded-lg bg-danger-100 p-3 text-sm text-danger-600">{query.error}</div>
            <button type="button" onClick={query.reload}
              className="min-h-11 rounded-lg border border-neutral-300 bg-white px-4 text-sm font-medium text-neutral-800 active:bg-neutral-100">
              {hi ? 'पुनः प्रयास करें' : 'Try again'}
            </button>
          </div>
        ) : exp?.paymentStatus === 'UNPAID' ? (
          /* Unpaid / Pre-purchase Card */
          <div className="space-y-4 rounded-xl border border-neutral-200 bg-neutral-50 p-5">
            <div>
              <h3 className="text-base font-semibold text-neutral-900">
                {hi ? 'अपनी स्वास्थ्य रिपोर्ट को सरल भाषा में समझें' : 'Understand your report in plain language'}
              </h3>
              <p className="mt-1 text-sm text-neutral-700">
                {hi
                  ? 'कठिन मेडिकल शब्दों के बजाय सरल हिंदी में जानें कि आपकी जांच रिपोर्ट का क्या अर्थ है।'
                  : 'What each of your test results means, and what the doctor decided, without the medical words.'}
              </p>
            </div>

            <ul className="space-y-2 text-sm text-neutral-800">
              {(hi
                ? ['रक्त, श्रवण और शारीरिक परीक्षण का संपूर्ण विवरण', 'डॉक्टर के निर्णय और सावधानियों का स्पष्ट अर्थ', 'जीवनशैली और स्वास्थ्य सुधार के उपयोगी सुझाव', 'अगले चेकअप में डॉक्टर से पूछने योग्य महत्वपूर्ण प्रश्न']
                : ['Your blood, hearing and body tests explained', 'What the doctor’s decision and advice mean', 'Practical tips for day-to-day health', 'Questions to ask the doctor next time']
              ).map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <CircleCheck aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-success-600" />
                  {line}
                </li>
              ))}
            </ul>

            {exp.disclaimer && (
              <p className="rounded-lg border border-warning-600/30 bg-warning-100 p-3 text-xs leading-relaxed text-neutral-800">
                {exp.disclaimer}
              </p>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-200 pt-4">
              <div>
                <span className="text-xs text-neutral-600">{hi ? 'शुल्क' : 'Price'}</span>
                <p className="text-xl font-bold text-neutral-900">{price}</p>
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={handlePurchase}
                className="min-h-12 rounded-lg bg-primary-600 px-5 text-sm font-semibold text-white active:bg-primary-700 disabled:opacity-60"
              >
                {busy
                  ? (hi ? 'प्रक्रिया जारी है…' : 'Processing…')
                  : (hi ? `${price} का भुगतान करें` : `Pay ${price}`)}
              </button>
            </div>
            {checkoutUrl && (
              <div role="status" className="space-y-2 rounded-lg bg-primary-50 p-3 text-sm text-neutral-800">
                <p>{hi
                  ? 'नए टैब में भुगतान पूरा करें, फिर यहाँ "स्थिति जाँचें" दबाएँ। हम बैंक से जाँचते हैं।'
                  : 'Finish paying in the new tab, then tap "Check status". We confirm with the bank, not this page.'}</p>
                <div className="flex flex-wrap gap-2">
                  <a href={checkoutUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex min-h-11 items-center rounded-lg border border-neutral-300 bg-white px-4 font-medium">
                    {hi ? 'भुगतान पेज खोलें' : 'Open payment page'}
                  </a>
                  <button type="button" onClick={query.reload}
                    className="min-h-11 rounded-lg border border-neutral-300 bg-white px-4 font-medium">
                    {hi ? 'स्थिति जाँचें' : 'Check status'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : exp?.status === 'GENERATING' || exp?.status === 'PENDING' ? (
          <div role="status" className="space-y-3 py-10 text-center text-sm text-neutral-700">
            <div aria-hidden className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-600" />
            <p className="font-medium">
              {hi ? 'एआई स्पष्टीकरण तैयार किया जा रहा है…' : 'Your explanation is being prepared…'}
            </p>
            <p className="text-xs text-neutral-600">
              {hi ? 'इसमें एक मिनट लग सकता है।' : 'This can take a minute.'}
            </p>
            <button type="button" onClick={query.reload}
              className="min-h-11 rounded-lg border border-neutral-300 bg-white px-4 text-sm font-medium text-neutral-800 active:bg-neutral-100">
              {hi ? 'फिर से देखें' : 'Check again'}
            </button>
          </div>
        ) : exp?.status === 'FAILED' ? (
          <div className="rounded-xl border border-danger-600/30 bg-danger-100 p-5 text-sm">
            <h3 className="font-semibold text-danger-600">
              {hi ? 'स्पष्टीकरण तैयार करने में समस्या आई' : 'The explanation could not be prepared'}
            </h3>
            <p className="mt-1 text-neutral-800">{exp.generationError ?? (hi ? 'कुछ गलत हो गया।' : 'Something went wrong.')}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={handleRetry}
                className="min-h-11 rounded-lg bg-primary-600 px-4 font-medium text-white active:bg-primary-700 disabled:opacity-60"
              >
                {busy ? (hi ? 'प्रक्रिया जारी है…' : 'Trying again…') : (hi ? 'पुनः प्रयास करें' : 'Try again')}
              </button>
              {exp.paymentStatus === 'PAID' && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={handleRefund}
                  className="min-h-11 rounded-lg border border-neutral-300 bg-white px-4 font-medium text-neutral-800 active:bg-neutral-100 disabled:opacity-60"
                >
                  {hi ? `रिफंड का अनुरोध करें (${price})` : `Ask for a refund (${price})`}
                </button>
              )}
            </div>
          </div>
        ) : exp?.status === 'COMPLETED' && exp.content ? (
          /* Completed Explanation View */
          <div className="space-y-5">
            {/* Statutory Disclaimer */}
            <p className="rounded-xl border border-warning-600/30 bg-warning-100 p-3.5 text-xs leading-relaxed text-neutral-800">
              {exp.content.disclaimer}
            </p>

            <section className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
              <h3 className="text-sm font-semibold text-neutral-900">
                {hi ? 'स्वास्थ्य अवलोकन' : 'Overview'}
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-neutral-900">{exp.content.overview}</p>
            </section>

            {exp.content.flaggedSummary.length > 0 && (
              <section className="rounded-xl border border-warning-600/30 bg-warning-100 p-4">
                <h3 className="flex items-center gap-1.5 text-sm font-semibold text-warning-600">
                  <TriangleAlert aria-hidden className="h-4 w-4" />
                  {hi ? 'विशेष ध्यान देने योग्य परिणाम' : 'Results to talk to your doctor about'}
                </h3>
                <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm text-neutral-900">
                  {exp.content.flaggedSummary.map((item, idx) => (
                    <li key={idx} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </section>
            )}

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-neutral-900">
                {hi ? 'जांच विवरण (पैनल अनुसार)' : 'Your tests, group by group'}
              </h3>
              {exp.content.panels.map((panel) => (
                <div key={panel.code} className="space-y-3 rounded-xl border border-neutral-200 p-4">
                  <div>
                    <h4 className="text-sm font-semibold text-neutral-900">{panel.name}</h4>
                    <p className="mt-0.5 text-sm text-neutral-700">{panel.plainMeaning}</p>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {panel.cards.map((c, i) => (
                      <div key={i} className="rounded-lg border border-neutral-200 bg-neutral-50 p-2.5">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="text-sm font-medium text-neutral-800">{c.name}</span>
                          <span className="shrink-0 text-sm font-bold text-neutral-900">{c.value}</span>
                        </div>
                        <p className="mt-1 text-xs leading-snug text-neutral-700">{c.plainExplanation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </section>

            {exp.content.physicianAdvicePlain && (
              <section className="rounded-xl border border-primary-200 bg-primary-50 p-4">
                <h3 className="text-sm font-semibold text-neutral-900">
                  {hi ? 'डॉक्टर की सलाह एवं सावधानियां' : 'What the doctor advised'}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-neutral-900">{exp.content.physicianAdvicePlain}</p>
              </section>
            )}

            <section className="rounded-xl border border-neutral-200 p-4">
              <h3 className="text-sm font-semibold text-neutral-900">
                {hi ? 'दैनिक जीवनशैली एवं स्वास्थ्य सुझाव' : 'Tips for every day'}
              </h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-neutral-800">
                {exp.content.lifestyleTips.map((tip, idx) => <li key={idx}>{tip}</li>)}
              </ul>
            </section>

            <section className="rounded-xl border border-neutral-200 p-4">
              <h3 className="text-sm font-semibold text-neutral-900">
                {hi ? 'अगली बार डॉक्टर से क्या पूछें?' : 'Questions to ask your doctor'}
              </h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-neutral-800">
                {exp.content.questionsForDoctor.map((q, idx) => <li key={idx}>{q}</li>)}
              </ul>
            </section>
          </div>
        ) : exp ? (
          // A state this screen has no words for (a refund in progress, say)
          // used to render nothing at all under the header.
          <p className="rounded-lg bg-neutral-100 p-3 text-sm text-neutral-700">
            {hi ? 'यह स्पष्टीकरण अभी उपलब्ध नहीं है।' : 'This explanation is not available right now.'}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/**
 * What the worker's employer may read, said as it is for THIS company.
 *
 * This screen told everybody "your employer does not see them — they see only
 * your fitness decision". That is the company's choice, not a constant: under
 * FULL its HR reads the whole Health Check Report, values included, and the
 * consent notice the same worker agreed to says so. A privacy statement that
 * is false for some of the people reading it is worse than none, so the
 * sentence is chosen from the policy the server reports.
 *
 * Each line says only what that level gives (HealthReportService): FULL is the
 * document without the doctor's private advice; SUMMARY is the counts and the
 * fitness decision, never a value; NONE is the certificate alone.
 */
type EmployerAccess = 'FULL' | 'SUMMARY' | 'NONE';

const EMPLOYER_ACCESS: Record<EmployerAccess, string> = {
  FULL:
    'Your company has chosen to let its occupational health and HR team read your health check '
    + 'report too, including your test values. The doctor’s private advice to you is not shown to them.',
  SUMMARY:
    'Your employer’s occupational health and HR team see your fitness decision and a summary of your '
    + 'health check report — how many results were inside and outside the usual range — but not the values.',
  NONE:
    'Your employer does not see your results or your health check report. They see only your fitness '
    + 'decision and any work restrictions.',
};

/**
 * Shown while the policy is not known — still loading, offline, or an older
 * server. True whatever the policy is, which the old sentence was not.
 */
const EMPLOYER_ACCESS_UNKNOWN =
  'Who else may see them is set out in the consent notice you agreed to before your checkup.';

/**
 * How many results, and how they stand — every one accounted for.
 *
 * The card read "17 of 18 results in the usual range" above a report that
 * says 20 parameters: two results have no usual range to be inside or outside
 * of (a finding written in words, say), and were silently left out of both
 * numbers. They are counted now, and said.
 */
function resultsLine(s: HealthReportSummary['summary']): string {
  const unranged = Math.max(0, s.measured - s.withRange);
  if (!unranged) return `${s.inRange} of ${s.measured} results in the usual range`;
  return `${s.measured} results · ${s.inRange} of ${s.withRange} in the usual range · `
    + `${unranged} with no usual range to compare`;
}

/** Opening a report: a five-minute grant, then the bytes — never a durable link. */
async function openDocument(grantPath: string, mode: 'view' | 'download') {
  const grant = await api.get<{ url: string; reportNumber: string }>(grantPath);
  if (mode === 'view') { window.open(`${grant.url}?inline=1`, '_blank', 'noopener'); return; }
  const res = await fetch(grant.url, { credentials: 'include' });
  if (!res.ok) throw new Error('The report could not be fetched.');
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = href;
  a.download = `${grant.reportNumber.replace(/\//g, '-')}.pdf`;
  a.click();
  URL.revokeObjectURL(href);
}

const PRIMARY_BTN = 'inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-primary-600 px-4 text-sm font-medium text-white active:bg-primary-700 disabled:opacity-60';
/** Result cards the list opens with; results outside their range are always among them. */
const FIRST_RESULTS = 8;

const SECONDARY_BTN = 'inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-neutral-300 bg-white px-4 text-sm font-medium text-neutral-800 active:bg-neutral-100 disabled:opacity-60';

/**
 * Your health check report — the whole examination explained, in your own
 * copy. Issued when the doctor signs the decision; opened through a grant
 * that dies in minutes, like the certificate.
 */
function HealthReports({ reports }: { reports: HealthReportSummary[] }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [explanationReportId, setExplanationReportId] = useState<string | null>(null);

  async function open(id: string, mode: 'view' | 'download') {
    setBusy(`${mode}-${id}`); setError(null);
    try {
      await openDocument(`/me/health-reports/${id}/download`, mode);
    } catch (e) {
      setError(errorMessage(e) || 'The report could not be opened.');
    } finally { setBusy(null); }
  }

  const rows = reports.filter((r) => r.hasDocument);
  if (!rows.length) return null;
  return (
    <section className="space-y-2" aria-labelledby="health-report-h">
      <h2 id="health-report-h" className="text-sm font-semibold text-neutral-900">Your health check report</h2>
      {error && <div role="alert" className="rounded-lg bg-danger-100 px-4 py-2 text-sm text-danger-600">{error}</div>}
      <ul className="space-y-2">
        {rows.map((r) => {
          const out = r.summary.low + r.summary.high + r.summary.critical + r.summary.abnormal;
          return (
            <li key={r.id} className="rounded-xl border border-primary-200 bg-primary-50 p-4">
              {/* What it is and when, first; the number is for quoting back. */}
              <div className="flex items-start gap-3">
                <FileText aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-primary-700" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-neutral-900">{checkupType(r.examinationType)}</p>
                  <p className="text-xs text-neutral-700">
                    Issued {shortDate(r.issuedAt)}
                    {r.fitness ? ` · ${r.fitness.decisionLabel}` : ''}
                  </p>
                </div>
              </div>
              <p className="mt-2 text-sm text-neutral-800">
                {resultsLine(r.summary)}
                {out ? ` · ${out} to talk to your doctor about` : ''}
              </p>
              {r.status === 'AMENDED' && (
                <p className="mt-1 text-xs font-medium text-warning-600">Replaced by a newer report</p>
              )}
              <p className="mt-1 font-mono text-xs text-neutral-600">{r.reportNumber}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" disabled={busy !== null} onClick={() => open(r.id, 'view')} className={PRIMARY_BTN}>
                  {busy === `view-${r.id}` ? 'Opening…' : 'View report'}
                </button>
                <button type="button" disabled={busy !== null} onClick={() => open(r.id, 'download')} className={SECONDARY_BTN}>
                  <Download aria-hidden className="h-4 w-4" />
                  {busy === `download-${r.id}` ? 'Saving…' : 'Download'}
                </button>
                <button type="button" onClick={() => setExplanationReportId(r.id)} className={SECONDARY_BTN}>
                  <Sparkles aria-hidden className="h-4 w-4 text-primary-700" />
                  Explain in simple words
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {explanationReportId && (
        <AiExplanationModal
          reportId={explanationReportId}
          onClose={() => setExplanationReportId(null)}
        />
      )}
    </section>
  );
}

function LabReports({ reports }: { reports: LabReportSummary[] }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function open(id: string, mode: 'view' | 'download') {
    setBusy(`${mode}-${id}`); setError(null);
    try {
      await openDocument(`/me/lab-reports/${id}/download`, mode);
    } catch (e) {
      setError(errorMessage(e) || 'The report could not be opened.');
    } finally {
      setBusy(null);
    }
  }

  const rows = reports.filter((r) => r.hasDocument);
  if (!rows.length) return null;

  return (
    <section className="space-y-2" aria-labelledby="lab-reports-h">
      <h2 id="lab-reports-h" className="text-sm font-semibold text-neutral-900">Laboratory reports</h2>
      {error && (
        <div role="alert" className="rounded-lg bg-danger-100 px-4 py-2 text-sm text-danger-600">{error}</div>
      )}
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.id} className="rounded-xl border border-neutral-200 bg-white p-4">
            <p className="text-sm font-semibold text-neutral-900">
              {r.laboratory ? r.laboratory.name : 'Laboratory report'}
            </p>
            <p className="text-xs text-neutral-700">
              {checkupType(r.examinationType)}
              {r.finalisedAt ? ` · ${shortDate(r.finalisedAt)}` : ''}
            </p>
            <p className="mt-2 text-sm text-neutral-800">
              {r.lineCount} tests
              {r.abnormalCount ? ` · ${r.abnormalCount} outside the usual range` : ' · all in the usual range'}
            </p>
            {r.status === 'AMENDED' && (
              <p className="mt-1 text-xs font-medium text-warning-600">Replaced by a newer report</p>
            )}
            <p className="mt-1 font-mono text-xs text-neutral-600">{r.reportNumber}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" disabled={busy !== null} onClick={() => open(r.id, 'view')} className={SECONDARY_BTN}>
                {busy === `view-${r.id}` ? 'Opening…' : 'View'}
              </button>
              <button type="button" disabled={busy !== null} onClick={() => open(r.id, 'download')} className={SECONDARY_BTN}>
                <Download aria-hidden className="h-4 w-4" />
                {busy === `download-${r.id}` ? 'Saving…' : 'Download'}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface ProfileReportRow { id: string; orderId: string; profileName: string; reportNumber: string; revision: number; stage: string; issuedAt: string }

/**
 * One document per test group — blood count, sugar, lipid profile, ECG … —
 * each with the names of the people who performed, verified and reviewed it.
 * Only issued documents are listed; nothing here is a draft.
 */
function ProfileReports() {
  const list = useQuery<ProfileReportRow[]>(() => api.get('/me/profile-reports'), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rows = list.data ?? [];
  if (!rows.length) return null;
  const orders = [...new Set(rows.map((r) => r.orderId))];

  async function open(key: string, path: string, mode: 'view' | 'download') {
    setBusy(key); setError(null);
    try { await openDocument(path, mode); }
    catch (e) { setError(errorMessage(e) || 'The report could not be opened.'); }
    finally { setBusy(null); }
  }

  return (
    <section className="space-y-2" aria-labelledby="profile-reports-h">
      <h2 id="profile-reports-h" className="text-sm font-semibold text-neutral-900">Medical reports</h2>
      {error && <div role="alert" className="rounded-lg bg-danger-100 px-4 py-2 text-sm text-danger-600">{error}</div>}
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.id} className="rounded-xl border border-neutral-200 bg-white p-4">
            <p className="text-sm font-semibold text-neutral-900">{r.profileName}</p>
            <p className="text-xs text-neutral-700">
              {r.stage === 'FINAL' ? 'Reviewed by the physician' : 'Physician review pending'} · {shortDate(r.issuedAt)}
            </p>
            <p className="mt-1 font-mono text-xs text-neutral-600">{r.reportNumber}{r.revision > 1 ? ` · revision ${r.revision}` : ''}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" disabled={busy !== null} onClick={() => open(`v-${r.id}`, `/me/profile-reports/${r.id}/download`, 'view')} className={SECONDARY_BTN}>
                {busy === `v-${r.id}` ? 'Opening…' : 'View'}
              </button>
              <button type="button" disabled={busy !== null} onClick={() => open(`d-${r.id}`, `/me/profile-reports/${r.id}/download`, 'download')} className={SECONDARY_BTN}>
                <Download aria-hidden className="h-4 w-4" />
                {busy === `d-${r.id}` ? 'Saving…' : 'Download'}
              </button>
            </div>
          </li>
        ))}
      </ul>
      {orders.map((o) => rows.filter((r) => r.orderId === o).length > 1 && (
        <button key={o} type="button" disabled={busy !== null} onClick={() => open(`all-${o}`, `/me/profile-reports/orders/${o}/download-all`, 'download')} className={SECONDARY_BTN}>
          <Download aria-hidden className="h-4 w-4" />
          {busy === `all-${o}` ? 'Saving…' : `Download all (${rows.filter((r) => r.orderId === o).length})`}
        </button>
      ))}
    </section>
  );
}

export function Records() {
  const results = useOfflineQuery<Result[]>('reports', () => api.get('/me/reports'));
  // Two examinations are forty result cards, 3,900px of thumb. The list opens
  // on the results that need reading — anything outside its range — and the
  // first few of the rest; one button shows them all.
  const [allResults, setAllResults] = useState(false);
  const everyResult = results.data ?? [];
  const outside = everyResult.filter((r) => r.flag !== 'NORMAL');
  const firstResults = [...outside, ...everyResult.filter((r) => r.flag === 'NORMAL')]
    .slice(0, Math.max(FIRST_RESULTS, outside.length));
  const shownResults = allResults || everyResult.length <= FIRST_RESULTS ? everyResult : firstResults;
  // Fetched here rather than inside each section: the empty state further
  // down has to know whether a report was issued, or it contradicts one.
  const healthReports = useQuery<HealthReportSummary[]>(() => api.get('/me/health-reports'), []);
  const labReports = useQuery<LabReportSummary[]>(() => api.get('/me/lab-reports'), []);
  const access = useQuery<{ healthReportAccess: EmployerAccess }>(() => api.get('/me/health-reports/access'), []);

  const employerAccess = access.data ? EMPLOYER_ACCESS[access.data.healthReportAccess] : undefined;
  // A report in hand means results were released: saying "No results yet"
  // beneath it is the screen arguing with itself.
  const reportIssued =
    (healthReports.data ?? []).some((r) => r.hasDocument) || (labReports.data ?? []).some((r) => r.hasDocument);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-base font-semibold text-neutral-900">Your reports</h1>
        <p className="mt-1 text-sm text-neutral-700">
          Results appear here once the laboratory doctor has checked and released them.{' '}
          {employerAccess ?? EMPLOYER_ACCESS_UNKNOWN}
        </p>
      </div>

      <HealthReports reports={healthReports.data ?? []} />
      <LabReports reports={labReports.data ?? []} />
      <ProfileReports />

      <section className="space-y-2" aria-labelledby="results-h">
        <h2 id="results-h" className="text-sm font-semibold text-neutral-900">Test results</h2>

        {results.stale && results.cachedAt && (
          <div role="status" className="rounded-lg bg-neutral-100 px-3 py-2 text-xs text-neutral-700">
            Saved copy from {savedAgo(results.cachedAt)}. Newer results may be available.
          </div>
        )}

        {results.loading ? (
          <div role="status" aria-label="Loading" className="space-y-2">
            <div className="h-20 animate-pulse rounded-xl bg-neutral-100" />
            <div className="h-20 animate-pulse rounded-xl bg-neutral-100" />
          </div>
        ) : results.error ? (
          <div className="space-y-3">
            <div role="alert" className="rounded-lg bg-danger-100 px-4 py-3 text-sm text-danger-600">{results.error}</div>
            <button type="button" onClick={results.reload} className={SECONDARY_BTN}>Try again</button>
          </div>
        ) : !results.data?.length ? (
          reportIssued ? (
            <div className="rounded-xl border border-dashed border-neutral-300 px-5 py-8 text-center">
              <p className="text-sm font-medium text-neutral-800">Your results are in the report above</p>
              <p className="mt-1 text-sm text-neutral-600">
                They are not listed one by one here yet. Open the report to read every result.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-neutral-300 px-5 py-10 text-center">
              <p className="text-sm font-medium text-neutral-800">No results yet</p>
              <p className="mt-1 text-sm text-neutral-600">
                If you have just had your tests done, results are usually released within a few days.
                We will send you a notification when they are ready.
              </p>
            </div>
          )
        ) : (
          <ul className="space-y-2">
            {shownResults.map((r) => {
              // A written finding carries no judgement: nobody decided it was
              // normal, so it is not called normal here.
              const flag = r.freeText && r.flag === 'NORMAL'
                ? { text: 'Written by the examiner', tone: 'text-neutral-700', mark: '' }
                : FLAG_LABEL[r.flag] ?? { text: 'See your report', tone: 'text-neutral-700', mark: '' };
              return (
                <li key={r.id} className="rounded-xl border border-neutral-200 bg-white p-4">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="min-w-0 text-sm font-medium text-neutral-900">{r.test.name}</p>
                    <p className="shrink-0 text-right text-sm font-semibold tabular-nums text-neutral-900">
                      {value(r)}
                    </p>
                  </div>
                  <div className="mt-1 flex items-baseline justify-between gap-3">
                    {/* The flag is a plain-language phrase, not a lab
                        abbreviation. "CRITICAL_HIGH" tells a worker nothing
                        useful and reads as alarming; "much higher than usual
                        — talk to your doctor" is both true and actionable. */}
                    <p className={`text-xs font-medium ${flag.tone}`}>
                      {flag.mark && <span aria-hidden className="mr-1">{flag.mark}</span>}
                      {flag.text}
                    </p>
                    <p className="shrink-0 text-xs text-neutral-600">{shortDate(r.releasedAt)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {everyResult.length > shownResults.length && (
          <button type="button" onClick={() => setAllResults(true)} className={`${SECONDARY_BTN} w-full justify-center`}>
            Show all {everyResult.length} results
          </button>
        )}
      </section>

      <p className="text-xs text-neutral-600">
        Questions about a result? Speak to your occupational health team — they can explain what
        it means for you.
      </p>
    </div>
  );
}
